import React, { useCallback, useMemo, useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { FormattedMessage } from 'react-intl'
import Container from 'components/Container'
import Row from 'components/Row'
import Col from 'components/Col'
import Button from 'components/Button'
import Tooltip from 'components/Tooltip'
import InfoIcon from 'assets/icons/info-icon'
import AddNew from 'assets/icons/add-new'
import { BiodiversitySelectorWrapper, InfoContainer } from 'components/BiodiversitySelector/style'
import { TabsInner, TabItem, TabUnderline, DividerLine } from './style'
import SpeciesTabContent from './SpeciesTabContent'
import EcosystemsTabContent from './EcosystemsTabContent'
import { PageTitle, PageSubtitle, TooltipHintWrapper, PageSubtitleWrapper } from 'flow/SiteProposal/pages/About/style'
import styled from 'styled-components'
import { Divider } from '@mui/material'
import AddDetailsPressure from './AddDetailsPressure'
const PressureDetailsForm = React.lazy(() => import('./PressureDetailsForm'))
import { setCopyPayload, setActiveDetail, clearActivePastes } from './ecosystemCopyStore'
import {
  requestThreatScope,
  requestThreatSeverity
} from 'api/monitoring'

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 24px;
  margin-bottom: 24px;
`

const SelectedPressure = styled.h3`
  font-size: 18px;
  font-weight: 600;
  margin: 0;
  color: rgba(32, 32, 32, 0.9);
`

/* Keep your original filter that preserves hierarchy */
const flattenTree = (nodes, filter, depth = 0) => {
  return nodes.flatMap((node) => {
    const matchesFilter = filter === 'all' || node.kind === filter
    const children = node.children ? flattenTree(node.children, filter, depth + 1) : []
    const includeNode = matchesFilter || children.length
    if (!includeNode) {
      return []
    }
    return [
      {
        ...node,
        children,
        depth
      }
    ]
  })
}

const AddDetailsEffectTab = ({
  preselectedItems = [],
  setMainTab,
  setIncomingSelection,
  setEcosystemsCount,
  threatItemId,
  selectedSubTab,
  isEditable,
  parentTabSelected,
  onBackToPressure = () => {},
  onCancel = () => {},
  setIsModalOpen = () => {}
}) => {
  const params = useParams()
  const [subTab, setSubTab] = useState(() => parentTabSelected || 'species')
  const [localPreselectedItems, setLocalPreselectedItems] = useState(preselectedItems || null)
  const [loadingSpecies, setLoadingSpecies] = useState(false)
  const [loadingEcosystem, setLoadingEcosystem] = useState(false)
  const [isLocalModalOpen, setIsLocalModalOpen] = useState(false)
  const [filterApi, setFilterApi] = useState(() => parentTabSelected === 'ecosystems' ? 2 : 1)
  const closeModal = () => setIsLocalModalOpen(false)
  const [scopeOptions, setScopeOptions] = useState([])
  const [severityOptions, setSeverityOptions] = useState([])
  const [taxonScopeOptions, setTaxonScopeOptions] = useState([])
  const [taxonSeverityOptions, setTaxonSeverityOptions] = useState([])

  const getScopeSelection = async () => {
    const data = await requestThreatScope()
    const scopeArr = []
    const taxonScopeArr = []

    data.forEach(item => {
      const formattedItem = {
        value: item.id,
        label: item.name
      }

      taxonScopeArr.push(formattedItem)

      if (item.id !== 999) {
        scopeArr.push(formattedItem)
      }
    })

    setScopeOptions(scopeArr)
    setTaxonScopeOptions(taxonScopeArr)
  }

  const getSeveritySelection = async () => {
    const data = await requestThreatSeverity()
    const severityArr = []
    const taxonSeverityArr = []
    data.forEach(item => {
      const formattedItem = {
        value: item.id,
        label: item.name
      }

      taxonSeverityArr.push(formattedItem)

      if (item.id !== 999) {
        severityArr.push(formattedItem)
      }
    })

    setSeverityOptions(severityArr)
    setTaxonSeverityOptions(taxonSeverityArr)
  }

  useEffect(() => {
    getScopeSelection()
    getSeveritySelection()
  }, [])

  useEffect(() => {
    if (parentTabSelected) {
      setSubTab(parentTabSelected)
      setFilterApi(parentTabSelected === 'ecosystems' ? 2 : 1)
    }
  }, [parentTabSelected])

  useEffect(() => {
    setLocalPreselectedItems(preselectedItems || null)
  }, [preselectedItems])

  // Keep parent tab state in sync with local selected tab.
  useEffect(() => {
    try {
      if (subTab === 'species') {
        if (typeof selectedSubTab === 'function') {
          selectedSubTab('species')
        }
        setFilterApi(1)
      } else if (subTab === 'ecosystems') {
        if (typeof selectedSubTab === 'function') {
          selectedSubTab('ecosystems')
        }
        setFilterApi(2)
      }
    } catch (e) {}
  }, [subTab, selectedSubTab])

  // Fetch latest triggers for the selected tab.
  const fetchSpecies = useCallback(async (incomingPreselected = null, apiFilter = filterApi) => {
    const isEcosystemTab = Number(apiFilter) === 2
    try {
      const monitorThreatId = (threatItemId && (threatItemId.threatItemId ?? threatItemId)) || threatItemId || null
      if (!monitorThreatId) return
      if (isEcosystemTab) {
        setLoadingEcosystem(true)
      } else {
        setLoadingSpecies(true)
      }

      const monitoringId = params?.monitoringId || null
      const data = await (await import('api/monitoring')).requestAllThreatTriggers(monitorThreatId, apiFilter)
      // API may return object with data property;
      const payload = data && (data.data || data) ? (data.data ? data : { data: data }) : { data: [] }

      // Only merge with existing preselected if incoming payload is empty (fallback)
      try {
        const incoming = Array.isArray(payload.data) ? payload.data : []
          // Always filter server returned data by removedNodeIds
          const filteredIncoming = filterOutRemovedItems(incoming)

          const existingThreatId = (incomingPreselected && incomingPreselected.threatItemId) ||
            (preselectedItems && preselectedItems.threatItemId) ||
            (payload && payload.threatItemId) || null

          // If server returned non-empty data, use it as filtered
          if (filteredIncoming && filteredIncoming.length > 0) {
            // clear any persisted removed-node ids for this threat: server data is authoritative
            try {
              sessionStorage.removeItem(removedStorageKey)
            } catch (err) {}
            const serverPayload = { ...(payload || {}), data: filteredIncoming, threatItemId: existingThreatId }
            setLocalPreselectedItems(serverPayload)
            return
          }

          // If server returned empty array, treat that as authoritative UNLESS caller
          // explicitly provided an `incomingPreselected` payload with data (e.g., user-added items).
          const hasIncomingSelection = incomingPreselected && Array.isArray(incomingPreselected.data) && incomingPreselected.data.length > 0
          if (!hasIncomingSelection) {
            // server empty + no explicit incoming selection: clear persisted removals
            try {
              sessionStorage.removeItem(removedStorageKey)
            } catch (err) {}
            const emptyPayload = { ...(payload || {}), data: [], threatItemId: existingThreatId }
            setLocalPreselectedItems(emptyPayload)
            return
          }

          const existing = (Array.isArray(incomingPreselected.data) && incomingPreselected.data) || []
          // incomingPreselected provided
          try {
            sessionStorage.removeItem(removedStorageKey)
          } catch (err) {}

          const groupMap = new Map()
          const addGroup = (g) => {
            const key = g.taxonomicGroup || g.name || 'Ungrouped'
            if (!groupMap.has(key)) groupMap.set(key, { speciesMap: new Map(), raw: null })
            const entry = groupMap.get(key)
            const speciesMap = entry.speciesMap
            const candidateRaw = g.group_threats_triggers || g.raw || null
            if (!entry.raw && candidateRaw) entry.raw = candidateRaw
            if (Array.isArray(g.species)) {
              g.species.forEach((s) => {
                const sid = s && (s.historyId || s.id || s.siteSpeciesId || s.site_species_id || JSON.stringify(s))
                if (!speciesMap.has(sid)) speciesMap.set(sid, s)
              })
            }
          }
          existing.forEach(addGroup)
          incoming.forEach(addGroup)

          const merged = []
          groupMap.forEach((entry, taxon) => {
            const raw = entry.raw
            const groupTriggers = Array.isArray(raw) ? raw : raw ? [raw] : undefined
            merged.push({ taxonomicGroup: taxon, species: Array.from(entry.speciesMap.values()), group_threats_triggers: groupTriggers })
          })

          const filteredMerged = filterOutRemovedItems(merged)
          const mergedPayload = { ...(payload || {}), data: filteredMerged, threatItemId: existingThreatId }
          setLocalPreselectedItems(mergedPayload)
      } catch (e) {
        setLocalPreselectedItems(payload)
      }
    } catch (e) {
      // ignore errors silently for now
    } finally {
      if (isEcosystemTab) {
        setLoadingEcosystem(false)
      } else {
        setLoadingSpecies(false)
      }
    }
  }, [threatItemId, params?.id, params?.monitoringId, filterApi, preselectedItems])

  useEffect(() => {
    try {
      if (subTab === 'ecosystems') {
        // Prevent stale species payload from flashing in ecosystem tab.
        setLocalPreselectedItems((prev) => {
          if (prev && Array.isArray(prev.data) && prev.data.length === 0) return prev
          return { ...(prev || {}), data: [] }
        })
        fetchSpecies(null, 2)
        return
      }
      if (subTab === 'species') fetchSpecies(preselectedItems, 1)
    } catch (e) {}
  }, [preselectedItems, fetchSpecies, subTab])

  // Listen for external requests to refresh species data (e.g., after bulk delete)
  useEffect(() => {
    const handler = (e) => {
      try {
        fetchSpecies()
      } catch (err) {
        // ignore
      }
    }
    document.addEventListener('species:refresh', handler)
    return () => document.removeEventListener('species:refresh', handler)
  }, [fetchSpecies])

  useEffect(() => {
    document.body.style.overflow = 'auto'
    return () => {
      document.body.style.overflow = 'auto'
      document.body.classList.remove('ReactModal__Body--open')
      document.body.classList.remove('modal-open')
      document.body.classList.remove('overflow-hidden')
    }
  }, [])

  return (
    <Container>
      <Row>
        <Col xs={12} md={10} mdOffset={1}>
          <Header>
            <SelectedPressure style={{ marginTop: '46px' }}>
              <span style={{ marginRight: 8 }}>Selected Pressure:</span>
              <span>
                {(preselectedItems &&
                  (preselectedItems.displayName || preselectedItems.name || preselectedItems.label)) ||
                  threatItemId?.titleValue ||
                  ''}
              </span>
            </SelectedPressure>
            <Button
              type="secondary"
              height="40px"
              width="180px"
              onClick={() => {
                onBackToPressure(false)
                setIsLocalModalOpen(false)
                onCancel(false)
                // selectedSubTab('species')
                if (typeof selectedSubTab === 'function') {
                  selectedSubTab(subTab)
                }
                setCopyPayload(null)
                try { setActiveDetail(null) } catch (e) {}
                try { clearActivePastes() } catch (e) {}
              }}
            >
              &larr;&nbsp; <FormattedMessage id="backToPressureBtnText" />
            </Button>
          </Header>
          <Divider />
        </Col>
      </Row>
      <Row>
        <Col xs={12} md={10} mdOffset={1}>
          <BiodiversitySelectorWrapper style={{ padding: '0 80px' }}>
            <PageTitle
              style={{ marginTop: '35px', display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}
            >
              <FormattedMessage id="threatImpactTriggerTitle" />
            </PageTitle>
            <PageSubtitleWrapper style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
              <PageSubtitle>
                <FormattedMessage id="threatImpactTriggerSubTitle" />
              </PageSubtitle>
              <Tooltip
                placement="bottom"
                trigger={['hover']}
                align={{ offset: [0, 16] }}
                zIndex={9999}
                overlay={{
                  label: 'threatImpactTriggerTitle',
                  infoTooltip: 'threaInfoToolTip'
                }}
                type="big"
              >
                <TooltipHintWrapper>
                  <InfoIcon />
                </TooltipHintWrapper>
              </Tooltip>
            </PageSubtitleWrapper>
          </BiodiversitySelectorWrapper>
        </Col>
      </Row>
      <Row>
        <Col xs={12} md={10} mdOffset={1}>
          <BiodiversitySelectorWrapper style={{ padding: '0 80px' }}>
            <TabsInner
              style={{
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              <div style={{ display: 'flex', gap: 16, paddingLeft: 0, width: '50%' }}>
                <TabItem
                  isActive={subTab === 'species'}
                  onClick={() => {
                    setSubTab('species')
                    setCopyPayload(null)
                    try { setActiveDetail(null) } catch (e) {}
                    try { clearActivePastes() } catch (e) {}
                  }}
                  className="active"
                  style={{
                    position: 'relative',
                    padding: '15px 0px',
                    width: '30%',
                    display: 'flex',
                    alignItems: 'flex-end',
                    justifyContent: 'center',
                    textAlign: 'center'
                  }}
                >
                  <FormattedMessage id="species" />
                  {subTab === 'species' && <TabUnderline />}
                </TabItem>
                <TabItem
                  isActive={subTab === 'ecosystems'}
                  onClick={() => {
                    setSubTab('ecosystems')
                    setCopyPayload(null)
                    try { setActiveDetail(null) } catch (e) {}
                    try { clearActivePastes() } catch (e) {}
                  }}
                  style={{
                    position: 'relative',
                    padding: '15px 0px',
                    width: '30%',
                    display: 'flex',
                    alignItems: 'flex-end',
                    justifyContent: 'center',
                    textAlign: 'center'
                  }}
                >
                    <FormattedMessage id="ecosystems" />
                    {subTab === 'ecosystems' && <TabUnderline />}
                  </TabItem>
                  </div>

            </TabsInner>
            <DividerLine style={{ marginTop: '0' }} />
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '10px' }}>
              <Button
                type={!isEditable ? 'disabledPrimary' : 'primary'}
                withIcon
                height="40px"
                marginTop="0px"
                isDisabled={!isEditable}
                onClick={() => {
                  if (!isEditable) return;
                    const inModal = typeof document !== 'undefined' && (document.body.style.overflow || '').indexOf('hidden') !== -1
                    if (typeof setMainTab === 'function' && inModal) {
                      setMainTab('select')
                      return
                    }

                    setIsLocalModalOpen(true)
                    setCopyPayload(null)
                    try { setActiveDetail(null) } catch (e) {}
                    try { clearActivePastes() } catch (e) {}
                }}
              >
                <AddNew />
                <FormattedMessage id="addMoreTriggerBtnText" values={{ name: subTab }} />
              </Button>
            </div>

            {subTab === 'species' ? (
              loadingSpecies ? (
                <div style={{ padding: 24, textAlign: 'center' }}>
                  <span>Loading species...</span>
                </div>
              ) : (
                <SpeciesTabContent
                  onAddToEffect={(items) => {
                    if (typeof setIncomingSelection === 'function') setIncomingSelection(items)
                    if (typeof setMainTab === 'function') setMainTab('add')
                    setIsLocalModalOpen(false)
                  }}
                  preselectedItems={localPreselectedItems || preselectedItems}
                  threatItemId={threatItemId}
                  isEditable={isEditable}
                  isScopeOptions={scopeOptions}
                  isSeverityOptions={severityOptions}
                  isTaxonScopeOptions={taxonScopeOptions}
                  isTaxonSeverityOptions={taxonSeverityOptions}
                />
              )
            ) : (
              loadingEcosystem ? (
                <div style={{ padding: 24, textAlign: 'center' }}>
                  <span>Loading ecosystem...</span>
                </div>
              ): (
                <EcosystemsTabContent
                  onSelectionChange={({ ecosystems }) => {
                    if (typeof setEcosystemsCount === 'function') setEcosystemsCount(ecosystems || 0)
                  }}
                  threatItemId={threatItemId}
                  preselectedItems={localPreselectedItems || preselectedItems}
                  isEditable={isEditable}
                  isScopeOptions={scopeOptions}
                  isSeverityOptions={severityOptions}
                />
              )
            )}
          </BiodiversitySelectorWrapper>
        </Col>
      </Row>
      {isLocalModalOpen && (
        <>
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(255,255,255,0.95)',
              zIndex: 9998
            }}
          />
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '20px',
              overflow: 'auto',
              zIndex: 9999
            }}
          >
            <AddDetailsPressure
              isOpen={true}
              modalTitleId={undefined}
              autoSaveIcon={true}
              onCancel={closeModal}
              titleValue={
                (preselectedItems && (preselectedItems.displayName || preselectedItems.name || preselectedItems.label)) ||
                (threatItemId && threatItemId.titleValue) || ''
              }
              threatItemId={threatItemId}
              isEditable={isEditable}
              onClose={closeModal}
            >
              <React.Suspense fallback={<div style={{ padding: 24, textAlign: 'center' }}>Loading...</div>}>
                <PressureDetailsForm
                  preselectedItems={preselectedItems}
                  threatItemId={threatItemId}
                  titleValue={
                    (preselectedItems && (preselectedItems.displayName || preselectedItems.name || preselectedItems.label)) ||
                    (threatItemId && threatItemId.titleValue) || ''
                  }
                  isEditable={isEditable}
                  onCancel={closeModal}
                  parentTabSelected={subTab}
                  selectedSubTab={setSubTab}
                />
              </React.Suspense>
            </AddDetailsPressure>
          </div>
        </>
      )}
    </Container>
  )
}
export default AddDetailsEffectTab
